<template>
  <div class="custom-line-entries-frame">
    <div v-if="pricingSourceBadge" class="custom-pricing-source-label">
      <span class="custom-pricing-source-text">Pricing Source:</span>
      <span
        class="badge"
        :class="{
          'bg-primary': pricingSourceBadge.source === 'insurer',
          'bg-success': pricingSourceBadge.source === 'provider',
          'bg-warning text-dark': pricingSourceBadge.source === 'shop'
        }"
        :title="pricingSourceBadge.detail || ''"
      >{{ pricingSourceLabel(pricingSourceBadge.source) }}</span>
      <span v-if="orgDiscountBadge" class="badge bg-info text-dark ms-2"
        :title="`Set at ${orgDiscountBadge.firmName}, applied to ${orgDiscountBadge.scope}`">
        {{ orgDiscountBadge.percent }}% — {{ orgDiscountBadge.firmName }}
      </span>
    </div>
    <div class="custom-price-label">
      <span>* price reflects discounted amount</span>
    </div>
    <div class="row custom-header">
      <div class="col-4 custom-share">
        <button class="btn btn-primary btn-sm" :disabled="isShareDisabled" @click="shareLines()">
          <FontAwesomeIcon v-if="isSharing" icon="fa-solid fa-spinner" spin />
          <span :class="{ 'ms-2': isSharing }">Unlock Export</span>
        </button>
        <div class="custom-info">
          <a v-popover tab-index="0" data-bs-toggle="popover" data-bs-trigger="focus hover" data-bs-html="true"
            data-bs-sanitize="false" title="Information" :data-bs-content="shareInfo" class="btn btn-link"
            role="button">
            <i class="fas fa-info-circle"></i>
          </a>
        </div>
        <span v-if="message !== ''" class="custom-message">{{ message }}</span>
        <!-- <button class="btn btn-secondary btn-sm custom-complete" @click="setAsComplete()">
          <span>Set Complete</span>
        </button> -->
      </div>
      <div class="col-4 text-center">
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" name="radioFilter" id="radioAll" value="All" v-model="filter">
          <label class="form-check-label" for="radioAll">
            All
          </label>
        </div>
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" name="radioFilter" id="radioADASMap" value="ADAS Map"
            v-model="filter">
          <label class="form-check-label" for="radioADASMap">
            ADAS Map
          </label>
        </div>
        <div v-if="!isInsurer" class="form-check form-check-inline">
          <input class="form-check-input" type="radio" name="radioFilter" id="radioIVSWizard" value="IVS Wizard"
            v-model="filter">
          <label class="form-check-label" for="radioIVSWizard">
            IVS Wizard
          </label>
        </div>
        <div class="form-check form-check-inline">
          <!-- <input v-if="!isInsurer" class="form-check-input" type="radio" name="radioFilter" id="radioFIT"
            value="FIT REV" v-model="filter">
          <label v-if="!isInsurer" class="form-check-label" for="radioFIT">
            FIT REV
          </label> -->
        </div>
      </div>
      <div class="col-3">
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="checkbox" id="copyData" v-model="isCopy" @change="showClipboards()">
          <label class="form-check-label" for="copyData">
            Copy Data
          </label>
        </div>
        <button v-if="isCopy" class="btn btn-primary btn-sm" @click="resetCells()">Clear</button>
      </div>
      <div class="col-1 text-end">
        <button class="btn btn-primary btn-sm" @click="refreshLines()">Refresh</button>
      </div>
    </div>
    <div class="custom-line-entries">
      <ZTable v-model="lineEntries" class="table table-bordered table-striped" :is-single-row-detail="true"
        :is-header-fixed="isHeaderFixed" :sort-by="line" :order-by="asc" :columns="tableColumns"
        :isAllChecked="isAllChecked" @checkedAll="checkedAll">
        <template #tbody="props">
          <td v-if="!isDontAllowBPCPShareChecks" class="custom-column-60 text-center">
            <div class="custom-check-share">
              <input type="checkbox" class="custom-checkbox-medium" :value="props.row.isChecked"
                :checked="props.row.isChecked" true-value="true" false-value="false" @click="checkedItem(props.row)" />
              <FontAwesomeIcon v-if="props.row.isCaptured" icon="fas fa-check-circle" class="text-success"
                title="Sent to Estimating Platform" />
            </div>
          </td>
          <td class="custom-column-60 text-center">
            <div class="custom-check-share">
              <span>{{ props.row.line }}</span>
              <!-- <FontAwesomeIcon v-if="props.row.isComplete" icon="fas fa-user-check" class="text-success"
                title="User Completed" /> -->
              <FontAwesomeIcon v-if="props.row.isComplete" icon="fas fa-check-circle" class="text-success"
                title="Captured in Estimating Platform" />
            </div>
          </td>
          <td>{{ props.row.category }}</td>
          <td>{{ props.row.source }}</td>
          <td>
            {{ props.row.description }}
            <!-- #467: red DTC warning triangle, matching ADAS Map's result-v3. Shown when the
                 line's matrix trigger notice says "Check for DTC"; hover shows the same
                 "Please Read!" popover with the trigger notice HTML. -->
            <a v-if="props.row.isFlagged" v-popover tabindex="0" role="button"
              data-bs-toggle="popover" data-bs-trigger="focus hover" data-bs-html="true"
              data-bs-sanitize="false" title="Please Read!" :data-bs-content="props.row.triggerNotice"
              class="custom-dtc-triangle">
              <FontAwesomeIcon icon="fas fa-exclamation-triangle" />
            </a>
            <a v-if="isCopy" href="#" @click.prevent="copyIt(props.row, 'description')">
              <FontAwesomeIcon icon="fas fa-clipboard" />
            </a>
            <FontAwesomeIcon v-if="isCopy && isCopied(props.row, 'description')" icon="fas fa-check-circle"
              class="text-success ms-1" />
            <!-- #619: on a split line the diagnostic moves BELOW the level sublines (see the
                 matching block after them). The level names must sit on the same rows as their
                 amounts in the Price cell, and an extra line here would offset every name by
                 one row — pairing each amount with the wrong level. -->
            <div v-if="props.row.category === 'ADAS' && !splitComponents(props.row).length">
              <span class="custom-estimate-item">( {{ props.row.diagnostic }} )</span>
            </div>
            <!-- #474: preview of the insurer parts-code name(s) sent to CCC, when the inspection's insurer has a mapping.
                 #619: a compound "&" line is split into components for CCC, each with its own parts-code name, so
                 preview one subline per component; a normal line previews its single mapped name.
                 Every component gets a subline (not just mapped ones) so the split is always visible and the
                 name sublines stay row-aligned with the price sublines in the Price cell. Each carries its own
                 clipboard link: appraisers enter a split calibration as two separate estimate lines. -->
            <template v-if="splitComponents(props.row).length">
              <div v-for="component in splitComponents(props.row)" :key="component.componentCalibrationType"
                class="custom-split-subline custom-split-name">
                <small class="text-muted">
                  <template v-if="component.insurerServiceName">{{ inspection.insuranceName }}: </template>{{ componentLabel(component) }}
                </small>
                <a v-if="isCopy" href="#" class="ms-1" @click.prevent="copyComponent(props.row, component, 'description')">
                  <FontAwesomeIcon icon="fas fa-clipboard" />
                </a>
                <FontAwesomeIcon v-if="isCopy && isCopied(props.row, 'description', component)"
                  icon="fas fa-check-circle" class="text-success ms-1" />
              </div>
            </template>
            <div v-else-if="props.row.insurerServiceName">
              <small class="text-muted">{{ inspection.insuranceName }}: {{ props.row.insurerServiceName }}</small>
            </div>
            <!-- #619: the split line's diagnostic, relocated below the level sublines so the
                 sublines stay row-aligned with their amounts. -->
            <div v-if="props.row.category === 'ADAS' && splitComponents(props.row).length">
              <span class="custom-estimate-item">( {{ props.row.diagnostic }} )</span>
            </div>
          </td>
          <td class="custom-column-60 text-center">{{ props.row.quantity }}</td>
          <td class="custom-column-110 text-right">
            {{ formatNumber(props.row.price - props.row.discount, 2) }}
            <a v-if="isCopy" href="#" @click.prevent="copyIt(props.row, 'price')">
              <FontAwesomeIcon icon="fas fa-clipboard" />
            </a>
            <FontAwesomeIcon v-if="isCopy && isCopied(props.row, 'price')" icon="fas fa-check-circle"
              class="text-success ms-1" />
            <!-- #619: break a split line's combined amount down into its per-level amounts, each copyable on
                 its own. Nelson (#619): copying the combined price makes it impossible to enter the two
                 calibration charges as separate estimate lines, and the user must see what they're copying.
                 Order matches the description sublines above, so subline N's name and amount pair up. -->
            <div v-for="component in splitComponents(props.row)" :key="component.componentCalibrationType"
              class="custom-split-subline">
              <small class="text-muted">{{ formatNumber(componentNet(component), 2) }}</small>
              <a v-if="isCopy" href="#" class="ms-1" @click.prevent="copyComponent(props.row, component, 'price')">
                <FontAwesomeIcon icon="fas fa-clipboard" />
              </a>
              <FontAwesomeIcon v-if="isCopy && isCopied(props.row, 'price', component)" icon="fas fa-check-circle"
                class="text-success ms-1" />
            </div>
          </td>
          <td class="custom-column-100 text-right">{{ formatNumber(props.row.discount, 2) }}</td>
        </template>
        <template #detail="props">
          <LineDetail :line-entry="props.row" :is-copy="isCopy" />
        </template>
        <template #empty> Nothing found! </template>
      </ZTable>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { inspectionStore } from '@/stores/inspection'
import { lookupStore } from '@/stores/lookup'
import { locationStore } from '@/stores/location'
import { authStore } from '@/stores/auth'
import ZTable from '@/components/utilities/elements/Table.vue'
import LineDetail from './LineDetail.vue'
import { filter } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import globals from '@/plugins/globals'
import { scopeLabel } from '@/components/pricing/scopeLabels'
export default {
  name: 'LineEntries',
  components: {
    ZTable,
    LineDetail,
    FontAwesomeIcon
  },
  data() {
    return {
      isHeaderFixed: true,
      filterValue: 'All',
      isCopy: false,
      anyChecked: false,
      extensionId: window.config ? window.config.chromeExtensionId : '',
      message: '',
      isSharing: false,
      isAllChecked: false,
      shareInfo: 'This will prepare the selected ADAS MAP lines to be imported into CCC. IVS Wizard line items WILL NOT be shared with CCC. Once the ADAS MAP lines have been selected and shared, CCC users need to access the Diagnostic Guidelines or Workflow module within CCC, select imported lines and add to the CCC work file lines and then Save their work in CCC.'
      //shareInfo: 'This will prepare the selected ADAS MAP lines to be imported into CCC. IVS Wizard line items WILL NOT be shared with CCC. Once the ADAS MAP lines have been selected and shared, CCC users need to access the Diagnostic Guidelines or Workflow module within CCC, select imported lines and add to the CCC work file lines and then Save their work in CCC. For FIT/REV subscribers, by selecting and sharing FIT/REV line items, they will be auto-shared with the FIT/REV platform. If a FIT/REV user wants to edit any consumable line items, this must be performed in the FIT/REV platform.'
      // shareInfo: 'You need to have the OPUS IVS Blueprint Copilot Chrome Extension installed and enabled. In the same browser, ensure CCC One is open in another tab/window to the estimate screen for this RO.'
    }
  },
  mounted() {
    this.isSharing = false
    this.isCopy = false
    this.anyChecked = false
    this.isAllChecked = false
    this.message = ''
    this.resetCells()
  },
  emits: ['refresh'],
  computed: {
    ...mapState(inspectionStore, ['lineEntries', 'inspection', 'copiedCells', 'selectedCells', 'sentItems']),
    ...mapState(lookupStore, ['lookups']),
    ...mapState(locationStore, ['preferences']),
    tableColumns() {
      let columns = []
      let is = false
      if (this.preferences.length > 0) {
        let item = this.preferences.filter((x) => x.name === 'isDontAllowBPCPShareChecks')
        if (item.length > 0) {
          is = item[0].value === '1' || item[0].value.toUpperCase() === 'TRUE'
        }
      }
      if (is) {
        columns = [
          { field: 'line', label: 'Line', sortable: false, class: 'custom-column-60 text-center' },
          { field: 'category', label: 'Category', sortable: false },
          { field: 'source', label: 'Source', sortable: false },
          { field: 'description', label: 'Description', sortable: false },
          { field: 'quantity', label: 'Qty', sortable: false, class: 'custom-column-60 text-center' },
          { field: 'price', label: 'Price', sortable: false, class: 'custom-column-110 text-right' }
        ]
      } else {
        columns = [
          { field: 'isChecked', label: '', sortable: false, class: 'custom-column-60 text-center', isCheckAll: true },
          { field: 'line', label: 'Line', sortable: false, class: 'custom-column-60 text-center' },
          { field: 'category', label: 'Category', sortable: false },
          { field: 'source', label: 'Source', sortable: false },
          { field: 'description', label: 'Description', sortable: false },
          { field: 'quantity', label: 'Qty', sortable: false, class: 'custom-column-60 text-center' },
          { field: 'price', label: 'Price', sortable: false, class: 'custom-column-110 text-right' },
          { field: 'discount', label: 'Discount', sortable: false, class: 'custom-column-100 text-right' }
        ]
      }
      return columns
    },
    isDontAllowBPCPShareChecks() {
      let _is = false
      if (this.preferences.length > 0) {
        let item = this.preferences.filter((x) => x.name === 'isDontAllowBPCPShareChecks')
        if (item.length > 0) {
          _is = item[0].value === '1' || item[0].value.toUpperCase() === 'TRUE'
        }
      }
      return _is
    },
    pricingSourceBadge() {
      if (!this.inspection) return null
      const source = this.inspection.pricingSource
      if (!source) return null
      return { source, detail: this.inspection.pricingDetail }
    },
    orgDiscountBadge() {
      if (!this.inspection) return null
      const firmName = this.inspection.discountSource
      const percent = this.inspection.discountPercent
      if (!firmName || !percent) return null
      return {
        firmName,
        percent,
        scope: this.orgDiscountScopeLabel(this.inspection.discountScope)
      }
    },
    isInsurer() {
      let _is = false
      if (this.preferences.length > 0) {
        let item = this.preferences.filter((x) => x.name === 'isInsurer')
        if (item.length > 0) {
          _is = item[0].value === '1' || item[0].value.toUpperCase() === 'TRUE'
        }
      }
      return _is
    },
    filter: {
      get() {
        return this.filterValue
      },
      set(value) {
        this.filterValue = value
        console.log('Filter changed to:', value)
        // update records based on the selected filter
        this.filterLineEntries(this.filterValue)
      }
    },
    systemTypes() {
      let _data = []
      if (this.lookups['SystemType']) {
        _data = this.lookups['SystemType'].filter((item) => item.isActive)
        _data = _data.map((item) => {
          return {
            id: item.id,
            name: item.name,
            code: item.code
          }
        })
      }
      return _data
    },
    isShareDisabled() {
      let _is = false
      if (this.lineEntries && this.lineEntries.length == 0) {
        /* If we don't have any line entries we can't share anything! */
        _is = true
      } else if (this.isSharing) {
        /* If we are actively sharing i.e. sending data */
        _is = true
      } else if (!this.isDontAllowBPCPShareChecks && !this.anyChecked) {
        /* If you ARE allowed to select individual records and you have none selected*/
        _is = true
      }
      return _is
    }
  },
  methods: {
    ...mapActions(inspectionStore, ['resetCells', 'cellAction', 'uncheckLineEntries', 'resetItems',
      'setItem', 'checkAllLineEntries', 'saveSentItems', 'filterLineEntries', 'sendLineItems', 'sendSelectedToFIT']),
    showClipboards() {
      console.log('Copy Data:', this.isCopy)
    },
    checkedItem(row) {
      this.message = ''
      row.isChecked = !row.isChecked
      this.anyChecked = this.lineEntries.some((entry) => entry.isChecked)
    },
    checkedAll(value) {
      this.isAllChecked = value
      this.checkAllLineEntries(value)
      this.anyChecked = value
    },
    isShow() {
      return this.isCopy
    },
    formatNumber(value, decimals) {
      return parseFloat(value).toFixed(decimals)
    },
    pricingSourceLabel(source) {
      switch (source) {
        case 'insurer': return 'Insurance'
        case 'provider': return 'Provider'
        case 'shop': return 'Shop'
        default: return source
      }
    },
    orgDiscountScopeLabel(scope) {
      // Lowercase only the first letter for mid-sentence use; "ADAS-L" stays as is.
      const label = scopeLabel(scope)
      return label ? label.charAt(0).toLowerCase() + label.slice(1) : label
    },
    insurerServiceNameFor(row) {
      // The insurer parts-code name for a line: the top-level mapped name for a normal
      // line, or (for a #619 split compound "&" line) the primary component's name —
      // the primary is the highest-ranked component (rank 1 = single highest-priced
      // calibration), matching the "Calibration - Level X" vs "Additional ..." split.
      if (row.insurerServiceName) return row.insurerServiceName
      if (Array.isArray(row.components)) {
        const named = row.components.filter((c) => c && c.insurerServiceName)
        if (named.length) {
          const primary = named.reduce((a, b) =>
            (b.rank ?? Number.MAX_SAFE_INTEGER) < (a.rank ?? Number.MAX_SAFE_INTEGER) ? b : a
          )
          return primary.insurerServiceName
        }
      }
      return ''
    },
    // #619: the component rows of a split compound "&" line, or [] when the line isn't
    // split. A line only carries components when DiagnosticLines_Get's conditional-split
    // gate found a price for every one of them, so a component price here is always real
    // money, never a derived zero. Fewer than two components isn't a split worth showing.
    splitComponents(row) {
      if (!Array.isArray(row.components) || row.components.length < 2) return []
      return row.components
    },
    // What an appraiser types into the estimate for one split level: the insurer
    // parts-code name when the inspection's insurer has a mapping for that component
    // (DiagnosticLines_Get leaves insurerServiceName null when it doesn't), else the
    // component's own calibration type name — the same fallback the quote uses.
    componentLabel(component) {
      return component.insurerServiceName || component.name || ''
    },
    componentNet(component) {
      return (component.price || 0) - (component.discount || 0)
    },
    // Copied-cell bookkeeping key. A component gets its own key per field so each subline
    // tracks its own green check and its own contribution to the running total.
    cellKey(row, field, component) {
      return component ? `${row.line}-${field}-${component.componentCalibrationType}` : `${row.line}-${field}`
    },
    copyIt(row, field) {
      let selectedText = window.getSelection().toString().trim()
      let textToCopy = selectedText || row[field]
      if (field === 'price') {
        textToCopy = this.formatNumber(row['price'] - row['discount'], 2)
      } else if (field === 'description' && !selectedText) {
        // #474: appraisers paste the copied description straight into a CCC estimate,
        // so copy the insurer parts-code variant (what we send via the quote, shown as
        // the subscript) when the line is mapped; fall back to the raw description.
        // #619: a split compound "&" line has no top-level variant — its name lives on
        // the primary (highest-ranked) component; copy that.
        const variant = this.insurerServiceNameFor(row)
        if (variant) textToCopy = variant
      }
      this.writeCell(row, field, null, textToCopy)
    },
    // #619: copy one split level on its own — its own parts-code name, or its own net
    // amount. The line-level buttons keep their existing meaning (primary name, combined
    // amount); these are what let the two levels go onto the estimate as separate lines.
    // Deliberately ignores any text selection: this button means "copy this subline".
    copyComponent(row, component, field) {
      const textToCopy = field === 'price'
        ? this.formatNumber(this.componentNet(component), 2)
        : this.componentLabel(component)
      this.writeCell(row, field, component, textToCopy)
    },
    writeCell(row, field, component, textToCopy) {
      if (textToCopy === null || textToCopy === undefined || textToCopy === '') return
      const key = this.cellKey(row, field, component)
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          window.getSelection().removeAllRanges()
          // Mark the cell as copied
          this.cellAction({
            name: 'copy',
            page: 1,
            idx: row.line,
            value: key
          })
          // if it's a number, add it to our total so far
          if (globals.isNumeric(textToCopy.toString().replace(/[$,]/g, ''))) {
            this.cellAction({
              name: 'select',
              page: 1,
              idx: row.line,
              value: textToCopy,
              key: key
            })
          }
        })
        .catch((err) => console.error('Clipboard copy failed:', err))
    },
    isCopied(row, field, component) {
      let _is = false
      const key = this.cellKey(row, field, component)
      let found = this.copiedCells.find((cell) => cell.page === 1 && cell.idx === row.line && cell.data === key)
      if (found !== null && found !== undefined) {
        _is = true
      }
      return _is
    },
    getLine(row) {
      let data = null
      if (this.inspection) {
        if (this.inspection.results) {
          data = this.inspection.results.find(entry => entry.vehicleAdas === row.id)
        }
      }
      return data
    },
    async sendMessageAsync(extensionId, message) {
      return new Promise((resolve, reject) => {
        if (!window.chrome?.runtime?.sendMessage) {
          this.message = 'Chrome extension messaging not available.'
          console.warn('Chrome extension messaging not available.')
          reject(new Error('Chrome extension messaging not available.'))
          return
        }
        chrome.runtime.sendMessage(extensionId, message, (response) => {
          if (chrome.runtime.lastError) {
            this.message = 'Error communicating with extension: ' + chrome.runtime.lastError.message
            console.error('Error communicating with extension:', chrome.runtime.lastError.message)
            reject(chrome.runtime.lastError)
          } else {
            resolve(response)
          }
        })
      })
    },
    lookupCCCItem(systemType) {
      let item = null
      if (this.lookups && systemType) {
        if (this.lookups['SystemType'] && this.lookups['CCCItemType']) {
          let found = this.lookups['SystemType'].find((item) => parseInt(item.value) === parseInt(systemType))
          if (found) {
            found = this.lookups['CCCItemType'].find((item) => parseInt(item.value) === parseInt(found.codename))
            if (found) {
              item = found.name
            } else {
              console.error(`No CCC Item Type found for System Type: ${systemType}`)
            }
          } else {
            console.error(`No System Type found for code: ${systemType}`)
          }
        } else {
          console.error('SystemType or CCCItemType lookup not available.')
        }
      } else {
        console.error('Lookups not available or systemType is null.')
      }
      return item
    },
    // async setAsComplete() {
    //   this.resetItems()
    //   // user is forcing a completion...
    //   let selectedLines = this.lineEntries.filter(entry => entry.isChecked)
    //   if (selectedLines.length > 0) {
    //     // send saved items (checked to be completed)
    //     for (let i = 0; i < selectedLines.length; i++) {
    //       let line = selectedLines[i]
    //       this.setItem({
    //         adasInspection: this.inspection.adasInspection,
    //         lineNo: line.id,
    //         item: line.item,
    //         system: line.system,
    //         location: line.location,
    //         description: line.description,
    //         diagnostic: line.diagnostic,
    //         qty: line.quantity,
    //         price: line.price,
    //         isCaptured: true,  // we are setting that we shared it
    //         isComplete: true,  // we are forcing completion
    //         source: line.source
    //       })
    //     }
    //     await this.saveSentItems()
    //     // reset checked lines
    //     this.uncheckLineEntries()
    //     this.anyChecked = false
    //     this.isAllChecked = false
    //     this.$emit('refresh')
    //   }
    // },
    refreshLines() {
      this.$emit('refresh')
    },
    async shareLines() {
      this.message = ''
      this.resetItems()

      // get checked lines
      let selectedLines = this.lineEntries.filter(entry => entry.isChecked)
      if (selectedLines.length > 0) {
        this.isSharing = true
        // send saved items (checked to be shared)
        for (let i = 0; i < selectedLines.length; i++) {
          let line = selectedLines[i]
          let netPrice = line.discount ? line.price - line.discount : line.price
          this.setItem({
            adasInspection: this.inspection.adasInspection,
            lineNo: line.id,
            item: line.item,
            system: line.system,
            location: line.location,
            description: line.description,
            diagnostic: line.diagnostic,
            qty: line.quantity,
            qtyType: line.item,
            price: netPrice,
            cost: netPrice,
            isCaptured: true, // we are setting that we shared it
            isComplete: line.isComplete,  // maintain what is complete (from BMS repair lines)
            source: line.source
          })
        }
        await this.saveSentItems()
        // send those lines to CCC One
        // let response = await this.sendLineItems(this.inspection.adasInspection)
        // this.isSharing = false
        // if (response) {
        //   if (response.error) {
        //     this.message = response.error
        //   } else {
        //     console.log('Send Line Items Response:', response)
        //   }
        // }
        //var response = null
        // now, send selected consumables to FIT REV
        // response = await this.sendSelectedToFIT()
        // if (response) {
        //   if (response.error) {
        //     this.isSharing = false
        //     this.message = response.error
        //   } else {
        //     console.log('Share Response:', response)
        //   }
        // }
        // if (this.message !== '') {
        //   this.isSharing = false
        //   return
        // }
        // reset checked lines
        this.uncheckLineEntries()
        this.anyChecked = false
        this.isAllChecked = false
        this.isSharing = false
        this.$emit('refresh')
        return
      }

    },
    async pushLines() {
      // NOTE: This method is no longer used. The shareLines() method is now used to send lines to CCC One and FIT REV.
      // This was the original method to push lines directly to CCC One via the extension.
      let proceed = false
      this.message = ''
      this.resetItems()
      this.isSharing = true
      // check to see if CCC One is open to the estimate page
      let response = await this.sendMessageAsync(this.extensionId, {
        action: 'estimatepage',
        data: null
      })
      if (response && response?.success) {
        if (response.result) {
          if (response.result.error) {
            this.message = response.result.error
            this.isSharing = false
            return
          }
          if (response.result.estimateTableCard) {
            proceed = response.result.estimateTableCard
          }
        } else {
          this.message = 'No estimate page found.'
        }
      } else {
        this.message = 'CCC One is not open and on an estimate page.'
      }
      if (proceed) {
        response = await this.sendMessageAsync(this.extensionId, {
          action: 'triggerDiagnostics',
          data: null
        })
        // check to see if we're in the Vehicle Diagnostics section
        if (response && response?.success) {
          if (response.result) {
            if (response.result.error) {
              this.message = response.result.error
              this.isSharing = false
              return
            }
            await globals.sleep(350)
            if (response.result.found) {
              // we need to loop through selections and push each line over to CCC One
              let selectedLines = this.lineEntries.filter(entry => entry.isChecked && entry.isComplete === false)
              if (selectedLines.length === 0) {
                this.message = 'No lines selected for sharing.'
              } else {
                console.log('Selected lines:', selectedLines)
                // loop through the selected lines and send each one to CCC One
                for (let i = 0; i < selectedLines.length; i++) {
                  let line = selectedLines[i]
                  // lookup the CCC Item for the system type
                  let cccItem = this.lookupCCCItem(line.systemType)
                  let price = line.price
                  if (cccItem) {
                    await globals.sleep(500)
                    // look up the CCC Item in the CCC One system under the Vehicle Diagnostics section
                    response = await this.sendMessageAsync(this.extensionId, {
                      action: 'selectdiagnostic',
                      data: {
                        item: cccItem
                      }
                    })
                    if (response && response?.success) {
                      if (response.result.error) {
                        console.error(response.result.error)
                      }
                      // now, enter the price for the selected item
                      await globals.sleep(500)
                      response = await this.sendMessageAsync(this.extensionId, {
                        action: 'enterdiagnostic',
                        data: {
                          item: price.toString()
                        }
                      })
                      if (response && response?.success) {
                        if (response.result) {
                          if (response.result.error) {
                            console.error(response.result.error)
                          } else {
                            // this is where we save what was sent over to CCC One
                            this.setItem({
                              adasInspection: this.inspection.adasInspection,
                              item: line.item,
                              system: line.system,
                              location: line.location,
                              description: line.description,
                              diagnostic: line.diagnostic,
                              price: line.price,
                              isCaptured: false,
                              isComplete: false
                            })
                          }
                        }
                      }
                    }
                  } else {
                    console.error('No CCC Item found for the selected lines.')
                  }
                }
                console.log('Items Sent:', this.sentItems)
                // save the sent items data
                await this.saveSentItems()
                // reset checked lines
                this.uncheckLineEntries()
                this.anyChecked = false
              }
            } else {
              this.message = 'No Vehicle Diagnostics section found.'
            }
          } else {
            this.message = 'No Vehicle Diagnostics section found.'
          }
        } else {
          this.message = 'No response from CCC One.'
        }
      }
      this.isSharing = false
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-header {
  padding: 13px;
  background-color: #f8f9fa;
  height: 50px;
}

.custom-line-entries-frame {
  position: relative;
}

.custom-line-entries {
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  max-height: 590px;
}

.custom-clipboard {
  color: black;
  transition: color 0.3s ease-in-out;
}

.custom-clipboard-copied {
  color: green !important;
}

.custom-share {
  position: relative;
}

.custom-info {
  position: absolute;
  top: -10px;
  left: 110px;
}

.custom-message {
  margin-left: 30px;
  color: red;
  font-size: 0.9em;
}

.custom-estimate-item {
  font-size: 0.8em;
  font-style: italic;
  color: #4e555a;
}

// #619: one split level of a compound "&" line — the name subline in the Description
// cell and its matching amount in the Price cell. The name is indented so the two levels
// read as parts of the line above them; the amount stays right-aligned with the column.
.custom-split-subline {
  line-height: 1.35;
}

.custom-split-name {
  padding-left: 10px;
}

// #467: red, pulsing DTC warning triangle — mirrors ADAS Map's .custom-triangle.
.custom-dtc-triangle {
  margin-left: 4px;
  color: red;
  font-size: 1.1em;
  cursor: pointer;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
}

.custom-check-share {
  position: relative;

  svg {
    position: absolute;
    font-size: 0.8em;
    top: -5px;
    right: -4px;
  }
}

.custom-complete {
  position: relative;
  float: right;
}

.custom-user {
  position: absolute;
  top: 0;
  right: 0;
  font-size: 0.7em;
}

.custom-pricing-source-label {
  position: absolute;
  top: -45px;
  right: 15px;
  font-size: 0.85em;
}

.custom-pricing-source-text {
  color: #6c757d;
  margin-right: 4px;
}

.custom-price-label {
  position: absolute;
  top: -25px;
  right: 15px;
  margin-bottom: 8px;
  font-size: 0.9em;
  color: #ff0000;
  font-style: italic;
}

</style>